import { TOptions } from 'i18next';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useApplication } from 'src/context/ApplicationContext';
import { StepId } from 'src/hooks/useWizardSteps';
import { StepOverrides } from '../i18n/types';

/**
 * Hook for managing client-configurable translations with fallback to defaults
 * Loads custom translations from an endpoint and falls back to default translations
 * if the key doesn't exist in the client configuration
 */
export function useStepTranslations(id?: StepId) {
  const { t, i18n } = useTranslation();

  const {
    steps: { activeStepId }
  } = useApplication();

  const stepId = id || activeStepId;

  if (!stepId) {
    throw new Error('[useStepTranslations] missing required argument: stepId');
  }

  const tStep = useCallback(
    (key: string, options?: TOptions): string => {
      if (!key) {
        throw new Error('[tStep] missing required argument: key');
      }

      const stepKey = [stepId, key].join('.');
      const stepKeyTranslation = t(stepKey, {
        ...options,
        ns: 'steps',
        returnObjects: false
      } as TOptions);

      if (stepKeyTranslation && stepKeyTranslation !== stepKey) {
        return stepKeyTranslation;
      }

      return t(key, options);
    },
    [stepId, t]
  );

  /**
   * Apply step-specific overrides to i18next
   * @param overrides - The step overrides to apply
   */
  const applyStepOverrides = useCallback(
    (overrides: StepOverrides) => {
      try {
        i18n.addResourceBundle('en', 'steps', overrides.en || {}, true, true);
        i18n.addResourceBundle('es', 'steps', overrides.es || {}, true, true);
      } catch (error) {
        console.warn('[applyStepOverrides] Failed to apply step overrides:', error);
      }
    },
    [i18n]
  );

  return {
    tStep,
    applyStepOverrides
  };
}
